import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSubscription, useSelectedAccountId } from '../../hooks/useSubscription'
import TimezoneDropdown from './TimezoneSection'

/**
 * PostIntervalSection Component
 *
 * Phase 3: Now uses account-level subscription limits for posting intervals
 */
function PostIntervalSection({ postIntervalHours, timezone, onIntervalChange, onTimezoneChange }) {
  const { t } = useTranslation('settings')
  const navigate = useNavigate()

  // Phase 3: Get selected account ID and its subscription limits
  const accountId = useSelectedAccountId()
  const { plan, limits } = useSubscription(accountId)

  const minAllowedInterval = limits?.min_post_interval_hours || 24

  // Determine which plan is required for each interval
  const getRequiredPlanForInterval = (hours) => {
    if (hours === 1) return 'pro_addon'  // PRO + 1h Add-on
    if (hours < 12) return 'pro'         // 2h, 4h, 8h → PRO
    if (hours === 12) return 'starter'   // 12h → STARTER
    return 'free'                        // 24h+ → FREE
  }

  // Common preset intervals for quick selection
  const PRESET_INTERVALS = [
    { hours: 1, label: t('interval.postInterval.presets.1h'), requiredPlan: 'pro_addon' },
    { hours: 2, label: t('interval.postInterval.presets.2h'), requiredPlan: 'pro' },
    { hours: 4, label: t('interval.postInterval.presets.4h'), requiredPlan: 'pro' },
    { hours: 8, label: t('interval.postInterval.presets.8h'), requiredPlan: 'pro' },
    { hours: 12, label: t('interval.postInterval.presets.12h'), requiredPlan: 'starter' },
    { hours: 24, label: t('interval.postInterval.presets.1d'), requiredPlan: 'free' },
    { hours: 48, label: t('interval.postInterval.presets.2d'), requiredPlan: 'free' },
    { hours: 72, label: t('interval.postInterval.presets.3d'), requiredPlan: 'free' },
    { hours: 168, label: t('interval.postInterval.presets.1w'), requiredPlan: 'free' }
  ]

  // Calculate posting frequency for display
  const frequency = useMemo(() => {
    if (postIntervalHours < 6) {
      const postsPerDay = Math.floor(24 / postIntervalHours)
      return `~${postsPerDay} Post${postsPerDay > 1 ? 's' : ''} pro Tag`
    }
    if (postIntervalHours < 24) {
      const postsPerWeek = Math.floor(168 / postIntervalHours)
      return `~${postsPerWeek} Posts pro Woche`
    }
    if (postIntervalHours === 24) {
      return '~1 Post pro Tag'
    }
    if (postIntervalHours < 168) {
      const postsPerWeek = Math.floor(168 / postIntervalHours)
      return `~${postsPerWeek} Post${postsPerWeek > 1 ? 's' : ''} pro Woche`
    }
    return '~1 Post pro Woche'
  }, [postIntervalHours])

  // Format hours for display
  const formatHours = (hours) => {
    if (hours < 24) {
      return `${hours} Stunde${hours > 1 ? 'n' : ''}`
    }
    if (hours === 24) {
      return '1 Tag'
    }
    if (hours % 24 === 0) {
      const days = hours / 24
      return `${days} Tag${days > 1 ? 'e' : ''}`
    }
    return `${hours} Stunden`
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('interval.title')}</h2>
        <p className="text-sm text-gray-600">
          {t('interval.subtitle')}
        </p>
      </div>

      {/* Timezone Dropdown */}
      <TimezoneDropdown
        timezone={timezone}
        onChange={onTimezoneChange}
      />

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('interval.postInterval.title')}</h3>
        <p className="text-sm text-gray-600">
          {t('interval.postInterval.subtitle')}
        </p>
      </div>

      {/* Preset buttons for quick selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">{t('interval.postInterval.quickSelect')}</label>
        <div className="grid grid-cols-4 gap-2">
          {PRESET_INTERVALS.map((preset) => {
            const isLocked = preset.hours < minAllowedInterval
            const isSelected = postIntervalHours === preset.hours

            return (
              <button
                key={preset.hours}
                type="button"
                onClick={() => {
                  if (isLocked) {
                    navigate('/subscription')
                  } else {
                    onIntervalChange(preset.hours)
                  }
                }}
                className={`
                  px-4 py-2 text-sm font-medium rounded-lg border transition-all relative
                  ${isSelected
                    ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                    : isLocked
                    ? 'bg-gray-50 text-gray-400 border-gray-300 cursor-pointer hover:border-primary-400'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-primary-500 hover:bg-gray-50'
                  }
                `}
                title={isLocked ? t('interval.postInterval.upsell.title') : ''}
              >
                <span className="flex items-center justify-center gap-1">
                  {preset.label}
                  {isLocked && (
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Tiered Upsell Banners */}
      {/* FREE → STARTER (for 12h) */}
      {plan === 'free' && minAllowedInterval > 12 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {t('interval.postInterval.upsell.toStarter.title')}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('interval.postInterval.upsell.toStarter.description')}
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t('interval.postInterval.upsell.toStarter.button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FREE/STARTER → PRO (for <12h but >1h) */}
      {(plan === 'free' || plan === 'starter') && minAllowedInterval > 2 && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {t('interval.postInterval.upsell.toPro.title')}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('interval.postInterval.upsell.toPro.description')}
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t('interval.postInterval.upsell.toPro.button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PRO → 1h Add-on */}
      {plan === 'pro' && minAllowedInterval > 1 && (
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {t('interval.postInterval.upsell.toAddon.title')}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('interval.postInterval.upsell.toAddon.description')}
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t('interval.postInterval.upsell.toAddon.button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Range slider for fine-tuning */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('interval.postInterval.custom')}
        </label>
        <div className="space-y-3">
          <input
            type="range"
            min={minAllowedInterval}
            max="168"
            value={Math.max(postIntervalHours, minAllowedInterval)}
            onChange={(e) => onIntervalChange(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
          />

          {/* Display current value */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-2xl font-bold text-primary-600">
                {formatHours(postIntervalHours)}
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">{frequency}</span>
              </div>
            </div>

            {/* Visual indicator */}
            <div className="flex items-center space-x-1">
              {[...Array(Math.min(7, Math.ceil(168 / postIntervalHours)))].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-8 bg-primary-400 rounded-sm"
                  style={{ opacity: 1 - (i * 0.1) }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Info boxes */}
      <div className="space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 text-sm text-blue-800">
              <p className="font-medium">{t('interval.postInterval.info.title')}</p>
              <p className="mt-1">
                {t('interval.postInterval.info.description')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 text-sm text-green-800">
              <p className="font-medium">{t('interval.postInterval.info.example.title')}</p>
              <p className="mt-1">
                {t('interval.postInterval.info.example.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Range labels */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span dangerouslySetInnerHTML={{ __html: t('interval.postInterval.slider.min').replace('\n', '<br/>') }} />
        <span dangerouslySetInnerHTML={{ __html: t('interval.postInterval.slider.mid').replace('\n', '<br/>') }} />
        <span dangerouslySetInnerHTML={{ __html: t('interval.postInterval.slider.max').replace('\n', '<br/>') }} />
      </div>
    </div>
  )
}

export default PostIntervalSection
